/**
 * Wippy package metadata types.
 *
 * **Layer boundary**: package.json is the **FE-author's view**. It is
 * source-relative and BE/router/FS agnostic. It describes the build
 * artifact (e.g. `browser: "dist/index.js"`, `wippy.path: "dist/app.html"`)
 * without knowing where the operator will serve it.
 *
 * The YAML registry entry (`wippy/views`) is the **operator's view**. It
 * is deployment-aware and owns paths that depend on the served layout.
 * YAML's `meta.entry_point` overrides the bundled wippy.path / browser
 * when the operator's serving pipeline reshapes the URL space (e.g.
 * `vite --outDir <static-dir>` flattens away the `dist/` prefix).
 *
 * YAML wins on every field. Bundled meta is the fallback. See
 * `wippy-framework/src/views/README.md` for the per-field mapping table.
 *
 * The consumer's `package.json` `wippy` block is the canonical FE-source
 * declaration. At build time the `@wippy-fe/vite-plugin` plugins resolve
 * any `file://<relative-path>` references inside the wippy block (file
 * contents replace the URI string) and emit `dist/wippy-meta.json` with
 * the resolved package.json.
 *
 * `wippy-meta.json` is the MANDATORY contract between consumers and
 * `wippy/views` ≥ 0.5.0. Without it, `views` falls back to its legacy
 * YAML-synthesis path (deprecated; exists only for migration). See
 * `web_components.spec.md` § "Package metadata: source of truth".
 *
 * Convention for `file://`-referenced files: name them
 * `<field-name-kebab>.do-not-link.<ext>` — the basename IS the kebab-case
 * spelling of the wippy block field the file populates. Examples:
 *   - `wippy.configOverrides.customization.customCSS` → `custom-css.do-not-link.css`
 *   - `wippy.description` → `description.do-not-link.md`
 *   - `wippy.<future-field>` → `<future-field-kebab>.do-not-link.<ext>`
 * The `.do-not-link.` infix is an imperative directive that consistently
 * prevents AI agents from `<link>`/`<script>`-ing the file directly in
 * app.html — empirically verified via blind tests. The proxy injects the
 * file's content at runtime from the resolved package metadata.
 */

import type { AppConfigOverrides } from '@shared/app-config/types'

export const SUPPORTED_WIPPY_TYPES = ['wippy-component-1.0'] as const
export const COMPONENT_TAG_SPECIFICATION = 'wippy-component-tag-1.0' as const
export type WippyType = typeof SUPPORTED_WIPPY_TYPES[number]

export interface WippyProps {
  type: 'object'
  properties: Record<string, {
    type: string
    default?: unknown
    description?: string
    [key: string]: unknown
  }>
}

export interface ProxyConfig {
  enabled: boolean
  injections: {
    css: {
      themeConfig: boolean
      iframe: boolean
      primevue: boolean
      markdown: boolean
      customCss: boolean
      customVariables: boolean
    }
    tailwindConfig: boolean
    resizeObserver: boolean
    preventLinkClicks: boolean
    iconifyIcons: boolean
    refreshWhenVisible: boolean
    historyPolyfill: boolean
    errorCapture: boolean
  }
}

// Scenario 1: ESM Module with Web Component
export interface WippyComponentConfig {
  tagName: string
  props: WippyProps
  scripts?: {
    build?: string
    debug?: string
    test?: string
  }
}

// Scenario 2: Web Page
export interface WippyPageConfig {
  path: string
  proxy: ProxyConfig
  configOverrides?: AppConfigOverrides
  scripts?: {
    build?: string
    debug?: string
    test?: string
  }
}

export type WippyConfig = WippyComponentConfig | WippyPageConfig

export interface PackageJsonBase {
  name: string
  version: string
  type?: string
  specification: string
  title?: string
  description?: string
  baseUrl?: string
}

export interface PackageJsonESM extends PackageJsonBase {
  browser: string
  wippy: WippyComponentConfig
  props?: Record<string, unknown>
}

export interface PackageJsonWebPage extends PackageJsonBase {
  wippy: WippyPageConfig
}

export function isWippyPackageESMModule(p: unknown, throwException: boolean = false): p is PackageJsonESM {
  const pkg = p as PackageJsonESM
  if (!SUPPORTED_WIPPY_TYPES.includes((pkg as PackageJsonESM).specification as WippyType)) {
    if (!throwException) {
      return false
    }
    throw new Error(`Unsupported Wippy package specification: ${pkg.specification}. Supported types: ${SUPPORTED_WIPPY_TYPES.join(', ')}`)
  }

  if (!pkg.wippy) {
    if (!throwException) {
      return false
    }
    throw new Error('Not a Wippy Compatible module: missing wippy configuration')
  }

  if (!pkg.wippy.props) {
    if (!throwException) {
      return false
    }
    throw new Error('Wippy configuration missing required props schema')
  }

  if (!pkg.browser) {
    if (!throwException) {
      return false
    }
    throw new Error('Wippy configuration missing entry point (browser) field')
  }

  return true
}

export interface PackageJsonComponent {
  specification: typeof COMPONENT_TAG_SPECIFICATION
  wippy: {
    tagName: string
  }
  props?: Record<string, unknown>
}

export function isWippyPackageComponent(p: unknown): p is PackageJsonComponent {
  const pkg = p as PackageJsonComponent
  return pkg?.specification === COMPONENT_TAG_SPECIFICATION
    && typeof pkg?.wippy?.tagName === 'string'
}

export function isWippyPackageWebPage(p: unknown, throwException: boolean = false): p is PackageJsonWebPage {
  const pkg = p as PackageJsonWebPage

  if (!SUPPORTED_WIPPY_TYPES.includes((pkg as PackageJsonWebPage).specification as WippyType)) {
    if (!throwException) {
      return false
    }
    throw new Error(`Unsupported Wippy package specification: ${pkg.specification}. Supported types: ${SUPPORTED_WIPPY_TYPES.join(', ')}`)
  }

  if (!pkg.wippy) {
    if (!throwException) {
      return false
    }
    throw new Error('Not a Wippy Compatible module: missing wippy configuration')
  }

  if (!pkg.wippy.path) {
    if (!throwException) {
      return false
    }
    throw new Error('Wippy configuration missing required path')
  }

  if (!pkg.wippy.proxy) {
    if (!throwException) {
      return false
    }
    throw new Error('Wippy configuration missing required proxy configuration')
  }

  return true
}
